import type { MapData } from '../types';

export interface SubmissionData {
  serialNumber: string;
  cityMap: string[]; // IDs das camadas ativas
  areaMap: string[];
  streetMap: string[];
  mapStyles: {
    city: 'color' | 'gray' | 'simple';
    area: 'color' | 'gray' | 'simple';
    street: 'color' | 'gray' | 'simple';
  };
  submissionDate: string;
  comments?: string;
  displayName?: string; // Nome amigável para exibição
}

// Chave para armazenar submissões no localStorage
const STORAGE_KEY = 'app_submissions';
const TRASH_KEY = 'app_submissions_trash';
const DISPLAY_NAMES_KEY = 'app_submission_names';

// Obtem os nomes de exibição do localStorage
function getDisplayNames(): Record<string, string> {
  try {
    const savedData = localStorage.getItem(DISPLAY_NAMES_KEY);
    if (savedData) {
      const parsed = JSON.parse(savedData);
      console.log("Nomes de exibição recuperados:", parsed);
      return parsed;
    }
  } catch (error) {
    console.error('Erro ao recuperar nomes de exibição:', error);
  }
  return {};
}

// Salva os nomes de exibição no localStorage
function persistDisplayNames(names: Record<string, string>): void {
  try {
    localStorage.setItem(DISPLAY_NAMES_KEY, JSON.stringify(names));
    console.log("Nomes de exibição salvos:", names);
  } catch (error) {
    console.error('Erro ao salvar nomes de exibição:', error);
  }
}

// Armazena os nomes de exibição
const displayNames = getDisplayNames();

// Obtem as submissões armazenadas do localStorage
function getSavedSubmissions(): Record<string, SubmissionData> {
  try {
    const savedData = localStorage.getItem(STORAGE_KEY);
    if (savedData) {
      const parsed = JSON.parse(savedData);
      console.log("Submissões recuperadas do localStorage:", Object.keys(parsed));
      
      // Adiciona os nomes de exibição, se existirem
      Object.keys(parsed).forEach(key => {
        if (displayNames[key]) {
          parsed[key].displayName = displayNames[key];
        }
      });
      
      return parsed;
    }
  } catch (error) {
    console.error('Erro ao recuperar submissões do localStorage:', error);
  }
  return {};
}

// Obtem as submissões removidas da "lixeira"
function getTrashedSubmissions(): Record<string, SubmissionData> {
  try {
    const trashData = localStorage.getItem(TRASH_KEY);
    if (trashData) {
      const parsed = JSON.parse(trashData);
      console.log("Submissões recuperadas da lixeira:", Object.keys(parsed));
      
      // Adiciona os nomes de exibição, se existirem
      Object.keys(parsed).forEach(key => {
        if (displayNames[key]) {
          parsed[key].displayName = displayNames[key];
        }
      });
      
      return parsed;
    }
  } catch (error) {
    console.error('Erro ao recuperar submissões da lixeira:', error);
  }
  return {};
}

// Salva as submissões no localStorage
function persistSubmissions(submissions: Record<string, SubmissionData>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(submissions));
    console.log("Submissões salvas no localStorage:", Object.keys(submissions));
  } catch (error) {
    console.error('Erro ao salvar submissões no localStorage:', error);
  }
}

// Salva as submissões na lixeira
function persistTrashedSubmissions(submissions: Record<string, SubmissionData>): void {
  try {
    localStorage.setItem(TRASH_KEY, JSON.stringify(submissions));
    console.log("Submissões salvas na lixeira:", Object.keys(submissions));
  } catch (error) {
    console.error('Erro ao salvar submissões na lixeira:', error);
  }
}

// Armazena as submissões feitas durante a sessão
const sessionSubmissions = getSavedSubmissions();
const trashedSubmissions = getTrashedSubmissions();

// Define ou atualiza o nome de exibição de uma submissão
export function setSubmissionDisplayName(serialNumber: string, displayName: string): void {
  if (!displayName.trim()) return;
  
  displayNames[serialNumber] = displayName;
  persistDisplayNames(displayNames);
  
  // Atualiza também no objeto da submissão se existir
  if (sessionSubmissions[serialNumber]) {
    sessionSubmissions[serialNumber].displayName = displayName;
    persistSubmissions(sessionSubmissions);
  } else if (trashedSubmissions[serialNumber]) {
    trashedSubmissions[serialNumber].displayName = displayName;
    persistTrashedSubmissions(trashedSubmissions);
  }
  
  console.log(`Nome de exibição para ${serialNumber} definido como "${displayName}"`);
}

// Obtém o nome de exibição de uma submissão
export function getSubmissionDisplayName(serialNumber: string): string | undefined {
  return displayNames[serialNumber];
}

// Esta função simula uma chamada à API para buscar uma submissão
// Em um ambiente de produção, seria substituída por uma chamada real
export async function fetchSubmissionBySerialNumber(serialNumber: string): Promise<SubmissionData | null> {
  // Simulando tempo de resposta da API
  await new Promise(resolve => setTimeout(resolve, 800));
  
  console.log(`Buscando submissão com número de série: ${serialNumber}`);
  
  // Validação do formato do número de série
  if (!serialNumber.startsWith('GRP|')) {
    throw new Error('Número de série inválido. Deve começar com GRP|');
  }
  
  // Verifica primeiro nas submissões salvas
  if (sessionSubmissions[serialNumber]) {
    // Adiciona o nome de exibição se existir e não estiver já na submissão
    if (displayNames[serialNumber] && !sessionSubmissions[serialNumber].displayName) {
      sessionSubmissions[serialNumber].displayName = displayNames[serialNumber];
    }
    return sessionSubmissions[serialNumber];
  }
  
  // Verifica também na lixeira
  if (trashedSubmissions[serialNumber]) {
    console.log(`Submissão ${serialNumber} encontrada na lixeira`);
    // Recupera automaticamente da lixeira
    sessionSubmissions[serialNumber] = trashedSubmissions[serialNumber];
    delete trashedSubmissions[serialNumber];
    
    // Adiciona o nome de exibição se existir
    if (displayNames[serialNumber]) {
      sessionSubmissions[serialNumber].displayName = displayNames[serialNumber];
    }
    
    // Persiste as mudanças
    persistSubmissions(sessionSubmissions);
    persistTrashedSubmissions(trashedSubmissions);
    
    console.log(`Submissão ${serialNumber} recuperada automaticamente da lixeira`);
    return sessionSubmissions[serialNumber];
  }
  
  // Simulação de submissões armazenadas
  // Em um ambiente real, esses dados viriam do backend
  const mockSubmissions: Record<string, SubmissionData> = {
    'GRP|2023-06-01|10:30:00|1234': {
      serialNumber: 'GRP|2023-06-01|10:30:00|1234',
      cityMap: ['bike-lanes', 'bike-paths', 'green-zones', 'metro'],
      areaMap: ['bike-lanes', 'danger-zones', 'gira-stations'],
      streetMap: ['bike-lanes', 'bike-parking', 'legenda-texto'],
      mapStyles: {
        city: 'color',
        area: 'gray',
        street: 'simple'
      },
      submissionDate: '01/06/2023, 10:30:00',
      comments: 'Proposta de grupo com foco em segurança para ciclistas'
    },
    'GRP|2023-06-02|14:45:00|5678': {
      serialNumber: 'GRP|2023-06-02|14:45:00|5678',
      cityMap: ['bike-paths', 'traffic', 'metro', 'train'],
      areaMap: ['bike-paths', 'bike-parking', 'points-of-interest'],
      streetMap: ['shared-paths', 'danger-zones', 'legenda-texto'],
      mapStyles: {
        city: 'gray',
        area: 'color',
        street: 'simple'
      },
      submissionDate: '02/06/2023, 14:45:00',
      comments: 'Abordagem com foco na integração com transportes públicos'
    }
  };
  
  // Verifica se a submissão existe no mock
  if (mockSubmissions[serialNumber]) {
    // Adiciona o nome de exibição se existir
    if (displayNames[serialNumber]) {
      mockSubmissions[serialNumber].displayName = displayNames[serialNumber];
    }
    return mockSubmissions[serialNumber];
  }
  
  return null;
}

// Função para salvar uma nova submissão na sessão atual
export function saveSubmission(
  serialNumber: string,
  mapsData: MapData[],
  mapStyles: Record<string, 'color' | 'gray' | 'simple'>
): void {
  // Extrai as camadas ativas de cada mapa
  const cityMap = mapsData[0].layers
    .filter(layer => layer.isActive)
    .map(layer => layer.id);
    
  const areaMap = mapsData[1].layers
    .filter(layer => layer.isActive)
    .map(layer => layer.id);
    
  const streetMap = mapsData[2].layers
    .filter(layer => layer.isActive)
    .map(layer => layer.id);
  
  // Cria o objeto de submissão com as camadas e estilos atuais
  const submission: SubmissionData = {
    serialNumber,
    cityMap,
    areaMap,
    streetMap,
    mapStyles: {
      city: mapStyles['city'],
      area: mapStyles['area'],
      street: mapStyles['street']
    },
    submissionDate: new Date().toLocaleString('pt-PT')
  };
  
  // Adiciona o nome de exibição se existir
  if (displayNames[serialNumber]) {
    submission.displayName = displayNames[serialNumber];
  }
  
  // Armazena a submissão no objeto de sessão
  sessionSubmissions[serialNumber] = submission;
  
  // Persiste as submissões no localStorage
  persistSubmissions(sessionSubmissions);
  
  console.log(`Submissão ${serialNumber} salva com sucesso:`, submission);
}

// Recupera todos os números de série disponíveis
export function getAllStoredSerialNumbers(): string[] {
  const keys = Object.keys(sessionSubmissions);
  console.log("getAllStoredSerialNumbers - chaves disponíveis:", keys);
  return keys;
}

// Recupera todos os números de série disponíveis, incluindo os da lixeira
export function getAllSerialNumbers(): string[] {
  const activeKeys = Object.keys(sessionSubmissions);
  const trashedKeys = Object.keys(trashedSubmissions);
  const allKeys = [...activeKeys, ...trashedKeys];
  console.log("getAllSerialNumbers - todas as chaves:", allKeys);
  return allKeys;
}

// Move uma submissão para a lixeira em vez de excluí-la
export function removeSubmission(serialNumber: string): void {
  if (sessionSubmissions[serialNumber]) {
    // Mover para a lixeira antes de remover
    trashedSubmissions[serialNumber] = sessionSubmissions[serialNumber];
    persistTrashedSubmissions(trashedSubmissions);
    
    // Remover da lista principal
    delete sessionSubmissions[serialNumber];
    persistSubmissions(sessionSubmissions);
    
    console.log(`Submissão ${serialNumber} movida para a lixeira`);
  }
}

// Recupera submissões da lixeira
export function recoverSubmissions(serialNumbers: string[]): string[] {
  const recoveredSerials: string[] = [];
  
  for (const serial of serialNumbers) {
    // Verifica se está na lixeira
    if (trashedSubmissions[serial]) {
      // Move de volta para as submissões ativas
      sessionSubmissions[serial] = trashedSubmissions[serial];
      delete trashedSubmissions[serial];
      recoveredSerials.push(serial);
    }
  }
  
  // Persistir as mudanças se houver recuperações
  if (recoveredSerials.length > 0) {
    persistSubmissions(sessionSubmissions);
    persistTrashedSubmissions(trashedSubmissions);
    console.log(`${recoveredSerials.length} submissões recuperadas da lixeira:`, recoveredSerials);
  }
  
  return recoveredSerials;
}

// Função para aplicar os dados de uma submissão a um conjunto de mapas
export function applySubmissionToMaps(
  mapsData: MapData[], 
  submission: SubmissionData
): MapData[] {
  return mapsData.map((map, index) => {
    let activeLayers: string[] = [];
    let styleToUse: 'color' | 'gray' | 'simple' = 'color';
    
    if (index === 0) { // city map
      activeLayers = submission.cityMap;
      styleToUse = submission.mapStyles.city;
    } else if (index === 1) { // area map
      activeLayers = submission.areaMap;
      styleToUse = submission.mapStyles.area;
    } else if (index === 2) { // street map
      activeLayers = submission.streetMap;
      styleToUse = submission.mapStyles.street;
    }
    
    // Base URL a ser usado baseado no estilo da submissão
    const baseUrl = 
      styleToUse === 'gray' 
        ? map.baseImageUrlGray 
        : styleToUse === 'simple' 
          ? map.baseImageUrlSimple 
          : map.baseImageUrl;
    
    return {
      ...map,
      baseImageUrl: baseUrl,
      layers: map.layers.map(layer => ({
        ...layer,
        isActive: activeLayers.includes(layer.id)
      }))
    };
  });
} 